import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ReleaseNote {
  key: number;
  type?: string;
  version: string;
  title: string;
  body: string;
  published?: boolean;
  publishDate?: string;
  priority?: number;
  icon?: string;
  targetType?: string;
  expiresAt?: string;
  dismissable?: boolean;
  isRead?: boolean;
  readDate?: string;
  timestampIns?: string;
  uteIns?: string;
  uteVar?: string;
  timestampVar?: string;
}

export interface NotificationTarget {
  userKey?: number;
  installation?: string;
  groupKey?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ReleaseNotesService {
  private apiUrl: string;

  constructor(private http: HttpClient) {
    const apiBaseUrl = environment['API_BASE_URL'] || process.env.API_BASE_URL;
    this.apiUrl = apiBaseUrl + 'crm';
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json');
  }

  // ============================================
  // USER ENDPOINTS
  // ============================================

  getUnread(userKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/release-notes/unread`, { userKey }, { headers: this.getHeaders() });
  }

  getUnreadCount(userKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/release-notes/unread-count`, { userKey }, { headers: this.getHeaders() });
  }

  markAsRead(userKey: number, releaseNoteKeys: number[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/release-notes/mark-read`, { userKey, releaseNoteKeys }, { headers: this.getHeaders() });
  }

  getHistory(userKey: number, page: number = 1, pageSize: number = 10): Observable<any> {
    return this.http.post(`${this.apiUrl}/release-notes/history`, { userKey, page, pageSize }, { headers: this.getHeaders() });
  }

  // ============================================
  // ADMIN ENDPOINTS (require MANAGE_RELEASE_NOTES right)
  // ============================================

  adminList(userKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/release-notes/list`, { userKey }, { headers: this.getHeaders() });
  }

  adminCreate(userKey: number, data: {
    version: string; title: string; body: string; published: boolean;
    targetType?: string; targets?: NotificationTarget[];
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/release-notes/create`, { userKey, ...data }, { headers: this.getHeaders() });
  }

  adminUpdate(userKey: number, releaseNoteKey: number, data: {
    version: string; title: string; body: string; published: boolean;
    targetType?: string; targets?: NotificationTarget[];
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/release-notes/update`, { userKey, releaseNoteKey, ...data }, { headers: this.getHeaders() });
  }

  adminDelete(userKey: number, releaseNoteKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/release-notes/delete`, { userKey, releaseNoteKey }, { headers: this.getHeaders() });
  }

  adminGetReaders(userKey: number, notifKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/release-notes/readers`, { userKey, notifKey }, { headers: this.getHeaders() });
  }

  adminGetTargets(userKey: number, releaseNoteKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/release-notes/targets`, { userKey, notifKey: releaseNoteKey }, { headers: this.getHeaders() });
  }
}
